import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../../context/API';
import type { DailyOperationsData, CustomerFeedbackData } from '../types';

export interface StaffReportsState {
  dailyOperations: DailyOperationsData | null;
  customerFeedback: CustomerFeedbackData | null;
  loading: {
    dailyOperations: boolean;
    customerFeedback: boolean;
  };
  error: string | null;
}

const initialState: StaffReportsState = {
  dailyOperations: null,
  customerFeedback: null,
  loading: {
    dailyOperations: false,
    customerFeedback: false,
  },
  error: null,
};

// Async thunks for fetching report data
export const fetchDailyOperationsReport = createAsyncThunk(
  'staffReports/fetchDailyOperations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('/staff/reports/daily-operations');
      return response.data.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch daily operations report'
      );
    }
  }
);

export const fetchCustomerFeedbackReport = createAsyncThunk(
  'staffReports/fetchCustomerFeedback',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('/staff/reports/customer-feedback');
      return response.data.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch customer feedback report'
      );
    }
  }
);

const staffReportsSlice = createSlice({
  name: 'staffReports',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetReports: (state) => {
      state.dailyOperations = null;
      state.customerFeedback = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Daily Operations
    builder
      .addCase(fetchDailyOperationsReport.pending, (state) => {
        state.loading.dailyOperations = true;
        state.error = null;
      })
      .addCase(fetchDailyOperationsReport.fulfilled, (state, action) => {
        state.loading.dailyOperations = false;
        state.dailyOperations = action.payload;
      })
      .addCase(fetchDailyOperationsReport.rejected, (state, action) => {
        state.loading.dailyOperations = false;
        state.error = action.payload as string;
      });

    // Customer Feedback
    builder
      .addCase(fetchCustomerFeedbackReport.pending, (state) => {
        state.loading.customerFeedback = true;
        state.error = null;
      })
      .addCase(fetchCustomerFeedbackReport.fulfilled, (state, action) => {
        state.loading.customerFeedback = false;
        state.customerFeedback = action.payload;
      })
      .addCase(fetchCustomerFeedbackReport.rejected, (state, action) => {
        state.loading.customerFeedback = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetReports } = staffReportsSlice.actions;
export default staffReportsSlice.reducer;
